import React, { useRef, useEffect, useState } from 'react';
import FamilyDialog from './FamilyDialog';
import type { FamilyToolsMember } from './types';
import { callManageFamily } from './manageFamilyClient';

interface MakePrimaryDialogProps {
  member: FamilyToolsMember;
  onClose: () => void;
  onDone: (result: { message: string; changedMemberIds: string[] }) => void;
}

/**
 * Confirms making `member` the family's primary (R5, SET_PRIMARY). Only
 * offered for adults — the modal that opens this dialog is responsible for
 * not offering it for a junior.
 */
const MakePrimaryDialog: React.FC<MakePrimaryDialogProps> = ({ member, onClose, onDone }) => {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  const handleConfirm = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      const result = await callManageFamily('SET_PRIMARY', {
        familyGroupId: member.familyGroupId,
        memberId: member.id,
      });
      if (result.success) {
        onDone({
          message: result.message || `${member.firstName} ${member.lastName} is now the family primary.`,
          changedMemberIds: result.changedMemberIds || [],
        });
      } else {
        setServerError(result.message || 'Could not change the family primary. Please try again.');
      }
    } catch (err: any) {
      setServerError(err?.message || 'Could not change the family primary. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FamilyDialog
      role="alertdialog"
      titleId="make-primary-title"
      title="Make primary?"
      descriptionId="make-primary-desc"
      busy={submitting}
      onClose={onClose}
    >
      <p id="make-primary-desc" className="mt-2 text-sm text-gray-700">
        Make <strong>{member.firstName} {member.lastName}</strong> the primary for this family?
        The current primary will no longer be the billing contact.
      </p>

      {serverError && (
        <p role="alert" className="mt-3 text-sm font-medium text-red-700">
          {serverError}
        </p>
      )}

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          ref={confirmRef}
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
          className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Updating…' : 'Make primary'}
        </button>
      </div>
    </FamilyDialog>
  );
};

export default MakePrimaryDialog;
